import { useEffect, useState } from "react";

import {
  deleteOrganizationUser,
  getOrganizationUsers,
} from "@/api/endpoints/organization";
import { postTrackingData } from "@/api/endpoints/track";
import PencilIcon from "@/assets/icons/pencil.svg";
import TrashIcon from "@/assets/icons/trash.svg";
import { USER_ACCESS } from "@/constants";
import ConfirmModal from "@/containers/ConfirmModal";
import EditUserModal from "@/containers/EditUserModal";
import { useMessages } from "@/containers/Messages/MessageContext";
import withRestriction from "@/hocs/withRestriction";
import { useOrganization } from "@/hooks/useOrganization";
import useSearchParams from "@/hooks/useSearchParams";
import { useUser } from "@/hooks/useUser";
import { EVENT } from "@/tracking_events";
import { ACCESS_LEVELS } from "@components/AccessControl";
import AllEarsTitle from "@components/AllEarsTitle";
import Button from "@components/Button";
import RoleDisplay from "@components/DataDisplay/RoleDisplay";
import Rhythm from "@components/Rhythm";
import Table from "@components/Table";
import Typography from "@components/Typography";

import styles from "./UsersView.module.css";

const UserRow = ({ onEdit, onDelete, user }) => {
  const { name, email, is_active } = user;
  const { data: userData } = useUser();
  const currentUser = userData?.ok ? userData.data : {};

  return (
    <tr data-testid="user-settings-user-row">
      <td>
        <Typography block margin="none">
          <strong>{name || "-"}</strong>
        </Typography>
        <Typography variant="body-2" block margin="none">
          {email}
        </Typography>
      </td>
      <td data-testid="user-settings-status" className={styles.desktopOnly}>
        {!is_active ? L.USER_STATUS_INVITED : L.USER_STATUS_ACTIVE}
      </td>
      <td align="right" className={styles.buttons}>
        <Button
          data-testid="user-settings-open-modal"
          icon={PencilIcon}
          onClick={() => onEdit(user)}
          variant="tertiary"
        />
        <Button
          data-test-id="user-settings-delete-user"
          disabled={currentUser.email === email}
          icon={TrashIcon}
          onClick={() => onDelete(user)}
          variant="tertiary"
        />
      </td>
    </tr>
  );
};

const UserTable = ({ users, ...props }) => {
  return (
    <div className={styles.tableWrapper}>
      <Table className={styles.table}>
        <thead>
          <tr>
            <th width="50%">{L.ORGANIZATION_USERS_COL_NAME}</th>
            <th width="30%" align="left" className={styles.desktopOnly}>
              {L.ORGANIZATION_USERS_COL_TYPE}
            </th>
            <th width="20%" align="right"></th>
          </tr>
        </thead>
        <tbody>
          {users.map((user, i) => (
            <UserRow user={user} {...props} key={i} />
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const UsersView = () => {
  const [{ user = undefined }, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
  const { danger, success } = useMessages();

  const { data: userData } = useUser();
  const me = userData?.ok ? userData.data : {};
  const { data: orgData } = useOrganization();
  const { uuid: organization_uuid } = orgData?.ok ? orgData.data : {};

  useEffect(() => {
    if (user && users) {
      const findBy = user === "me" ? me.email : user;
      setEditUser(users.find((u) => u.email === findBy));
    }
  }, [user, me.email, users]);

  // Fetch all users belonging to the current organization
  useEffect(() => {
    if (!organization_uuid) return;
    getOrganizationUsers(organization_uuid).then(({ data }) => {
      // The current user should be first in the list.
      setUsers(data.sort((a) => (a.email === me.email ? -1 : 1)));
    });
  }, [me.email, organization_uuid]);

  const updateUser = (_user) => {
    setUsers((_users) => _users.map((u) => (u.id === _user.id ? _user : u)));
  };

  const deleteUser = async (_user) => {
    deleteOrganizationUser({
      organization_uuid: organization_uuid,
      user_id: _user.id,
    })
      .then(({ status }) => {
        if (status > 200) {
          throw new Error();
        }
        setUsers((_users) => _users.filter((u) => u.id !== _user.id));
        success({
          message: L.ORGANIZATION_USERS_USER_DELETED,
        });
      })
      .catch(() => {
        danger({
          message: L.ORGANIZATION_USERS_USER_DELETE_FAILURE,
        });
      })
      .finally(() => {
        postTrackingData(EVENT.REMOVE_USER, { email: _user?.email });
        setConfirmDeleteUser(null);
      });
  };

  const onEditClose = () => {
    setSearchParams({ user: undefined });
    setEditUser(null);
  };

  const readOnlyUsers = users.filter(
    (u) => u.user_type === USER_ACCESS.READONLY
  );
  const regularUsers = users.filter((u) => u.user_type === USER_ACCESS.USER);
  const adminUsers = users.filter((u) => u.user_type === USER_ACCESS.ADMIN);

  const USER_DESCRIPTION_BY_TYPE = {
    [USER_ACCESS.ADMIN]: L.ROLE_DESCRIPTION_ADMIN,
    [USER_ACCESS.USER]: L.ROLE_DESCRIPTION_USER,
    [USER_ACCESS.READONLY]: L.ROLE_DESCRIPTION_READONLY,
  };

  return (
    <>
      <AllEarsTitle title={L.ORGANIZATION_USERS_TITLE} />
      <Rhythm>
        <Typography block variant="subtitle-2">
          {L.ORGANIZATION_USERS_TITLE}
        </Typography>
        <Typography className="text-muted">
          {L.ORGANIZATION_USERS_SUBTITLE}
        </Typography>
      </Rhythm>
      <Rhythm>
        {adminUsers.length > 0 && (
          <div className={styles.group}>
            <div className={styles.description}>
              <Typography>
                <strong>
                  <RoleDisplay plural id={USER_ACCESS.ADMIN} />
                </strong>
              </Typography>
              <Typography className="text-muted" block>
                {USER_DESCRIPTION_BY_TYPE[USER_ACCESS.ADMIN]}
              </Typography>
            </div>
            <UserTable
              users={adminUsers}
              onDelete={deleteUser}
              onEdit={setEditUser}
            />
          </div>
        )}
        {users.length > 0 && (
          <div className={styles.group}>
            <div className={styles.description}>
              <Typography>
                <strong>
                  <RoleDisplay plural id={USER_ACCESS.USER} />
                </strong>
              </Typography>
              <Typography className="text-muted" block>
                {USER_DESCRIPTION_BY_TYPE[USER_ACCESS.USER]}
              </Typography>
            </div>
            <UserTable
              users={regularUsers}
              onDelete={deleteUser}
              onEdit={setEditUser}
            />
          </div>
        )}
        {readOnlyUsers.length > 0 && (
          <div className={styles.group}>
            <div className={styles.description}>
              <Typography>
                <strong>
                  <RoleDisplay plural id={USER_ACCESS.READONLY} />
                </strong>
              </Typography>
              <Typography className="text-muted" block>
                {USER_DESCRIPTION_BY_TYPE[USER_ACCESS.READONLY]}
              </Typography>
            </div>
            <UserTable
              users={readOnlyUsers}
              onDelete={deleteUser}
              onEdit={setEditUser}
            />
          </div>
        )}
      </Rhythm>

      <EditUserModal
        onUpdateUser={updateUser}
        onHide={onEditClose}
        show={editUser !== null}
        user={editUser}
      />
      <ConfirmModal
        onConfirm={() => deleteUser(confirmDeleteUser)}
        show={confirmDeleteUser !== null}
        onHide={() => setConfirmDeleteUser(null)}
        title={L.ORGANIZATION_USERS_USER_DELETE_CONFIRM_TITLE}
        message={L.ORGANIZATION_USERS_USER_DELETE_CONFIRM_MESSAGE.replace(
          "{}",
          confirmDeleteUser?.full_name || confirmDeleteUser?.email
        )}
        onCancel={() => setConfirmDeleteUser(null)}
      />
    </>
  );
};

export default withRestriction(UsersView, { level: ACCESS_LEVELS.ADMIN });
